// Apolo IDE — useHandsFreeMode (Sprint v11 E15, hardened for fluid voice mode).
//
// Conversational voice loop:
//   0. Prefer full-duplex Realtime (GPT-Live): mic and model stay open at
//      once. semantic_vad owns barge-in vs backchannel; no walkie-talkie turns.
//   1. Fallback: listening → Web Speech or Realtime STT capture
//   2. silence detection ends the capture
//   3. live transcript deltas → committed final text
//   4. submit text via supplied onSubmitText callback
//   5. as the LLM streams, apolo:handsFreeReplyDelta delivers complete
//      sentences; the speech pump speaks sentence #1 while later ones are
//      still generating. apolo:handsFreeReply marks the reply complete.
//   6. on playback end, return to listening — or interrupt() (barge-in)
//      cuts it short and returns immediately
//
// Every network call (transcribe, tts) and every "wait for X" step
// (onSubmitText, apolo:handsFreeReply) has a recovery path: a thrown
// fetch(), a non-ok response, or a reply that never arrives all surface an
// error and go back to listening instead of freezing the loop silently —
// that silent freeze was the "voice isn't responding" failure mode.
//
// Wake word: if `wakeWord` is provided, the first transcript must contain
// it before we forward to the LLM. Subsequent turns drop the requirement
// until the loop is disabled.
//
// Toggle on/off via the returned { start, stop, interrupt, state }.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '../lib/apiBase.js';
import { getStoredToken } from '../lib/api.js';
import { isElectron } from '../lib/isElectron.js';
import { RealtimeTranscriber } from '../lib/realtimeTranscriber.js';
import { RealtimeConversation } from '../lib/realtimeConversation.js';
import { runVoiceTool } from '../lib/voiceTools.js';
import { safeFetch } from '../lib/safeFetch.js';

const SILENCE_RMS = 0.02;
// Hold after the user STOPS speaking. Must NOT fire before we ever heard voice
// (that was cutting MediaRecorder in ~500ms with an empty blob → "Transcrição vazia").
const SILENCE_HOLD_MS = 700;
// Ignore silence until we've had at least this much voiced audio — avoids
 // chopping the first syllable / committing an empty Realtime buffer.
const MIN_VOICED_MS = 450;
const MAX_CAPTURE_MS = 30_000;
// If the LLM turn never dispatches apolo:handsFreeReply (agent error, dropped
// event, empty finalText…) the loop used to hang in 'thinking' forever with
// no feedback — indistinguishable from "voice isn't responding". This is the
// recovery net: surface an error and go back to listening instead of freezing.
const REPLY_TIMEOUT_MS = 45_000;
// Auto-recovery (setError + go back to listening) used to call
// startCaptureRef.current() in the SAME synchronous tick as setError() —
// and startCapture()'s own first line is setError(null). React 18 batches
// same-tick updates, so only the final (null) value ever rendered: the error
// was set and immediately overwritten before the user ever saw it. Delaying
// the restart lets the error commit to a real render first.
const ERROR_RECOVER_MS = 1_800;

function startRmsSilenceMonitor(stream, onSilence) {
  const w = /** @type {*} */ (window);
  const context = new (w.AudioContext || w.webkitAudioContext)();
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const data = new Uint8Array(analyser.fftSize);
  let lastVoiceAt = Date.now();
  let heardVoice = false;
  let voicedMs = 0;
  let lastTickAt = Date.now();
  let active = true;

  function tick() {
    if (!active) return;
    const now = Date.now();
    const dt = Math.min(100, now - lastTickAt);
    lastTickAt = now;
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const value = (data[i] - 128) / 128;
      sum += value * value;
    }
    const rms = Math.sqrt(sum / data.length);
    if (rms > SILENCE_RMS) {
      heardVoice = true;
      voicedMs += dt;
      lastVoiceAt = now;
    } else if (
      heardVoice &&
      voicedMs >= MIN_VOICED_MS &&
      now - lastVoiceAt > SILENCE_HOLD_MS
    ) {
      active = false;
      onSilence();
      return;
    }
    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
  const maxTimer = setTimeout(() => {
    if (!active) return;
    active = false;
    onSilence();
  }, MAX_CAPTURE_MS);
  return {
    stop() {
      active = false;
      clearTimeout(maxTimer);
      if (context.state !== 'closed') context.close().catch(() => null);
    },
  };
}

// Pack sentences up to maxChunk — kept for callers/tests that prefer fewer
// TTS round-trips. The live speech pump uses splitSentences (one unit per
// sentence) so the first complete sentence can speak immediately.
export function splitIntoSpeechChunks(text, { maxChunk = 240 } = {}) {
  const s = String(text || '').trim();
  if (!s) return [];
  const parts = s
    .split(/(?<=[.!?…])\s+|\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const chunks = [];
  let buf = '';
  for (const p of parts) {
    if (!buf) buf = p;
    else if ((buf + ' ' + p).length <= maxChunk) buf += ' ' + p;
    else {
      chunks.push(buf);
      buf = p;
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

// Split text into individual sentence units for streaming TTS. Unlike a
// "pack sentences up to N chars" chunker, this keeps each sentence separate so
// a freshly-completed first sentence can be spoken IMMEDIATELY, without waiting
// for the next one — the whole point of low-latency streaming voice. Each unit
// is a run of text ending in a terminator (. ! ? … newline); a trailing run
// with no terminator is returned as the final (still-forming) unit.
export function splitSentences(text) {
  if (!text) return [];
  const matches = text.match(/[^.!?…\n]*[.!?…\n]+|[^.!?…\n]+$/g) || [];
  return matches.map((s) => s.trim()).filter(Boolean);
}

// True when `text` ends on a sentence terminator (ignoring trailing quotes /
// brackets / whitespace) — i.e. the last sentence is complete, not still forming.
function endsOnSentenceBoundary(text) {
  return /[.!?…\n]["'”’)\]]*\s*$/.test(text || '');
}

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  const w = /** @type {*} */ (window);
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

function hasSpeechSynthesis() {
  return (
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    typeof SpeechSynthesisUtterance !== 'undefined'
  );
}

// Strip markdown/code so spoken replies don't narrate syntax. Cheap + big
// UX/cost win: we never vocalize code blocks, stack traces, or URLs.
export function toSpokenText(text) {
  if (!text) return '';
  return text
    .replace(/```[\s\S]*?```/g, ' (bloco de código) ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[*_~>]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .slice(0, 1_200);
}

// Free TTS via SpeechSynthesis. Returns a Promise that resolves when speech
// ends (or errors). Caller guarantees synthesis is supported.
function speakNative(text, { lang = 'pt-BR', rate = 1.0 } = {}) {
  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang;
    utter.rate = rate;
    utter.onend = () => resolve();
    utter.onerror = () => resolve();
    synth.cancel(); // clear any queued utterance before speaking
    synth.speak(utter);
  });
}

// Plays one TTS audio blob and resolves when it ends. `refs.audioResolveRef`
// is stashed so interrupt() (barge-in) can resolve this early instead of
// waiting for onended — kept separate from the pump loop so its branching
// stays readable.
function playAudioBlob(blob, refs) {
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  refs.audioRef.current = audio;
  return new Promise((resolve) => {
    refs.audioResolveRef.current = resolve;
    const finish = () => {
      URL.revokeObjectURL(url);
      refs.audioResolveRef.current = null;
      resolve();
    };
    audio.onended = finish;
    audio.onerror = finish;
    audio.play().catch(finish);
  }).then(() => {
    refs.audioRef.current = null;
  });
}

// Plays one already-fetched chunk: OpenAI blob if we got one, else the free
// native voice as a degraded fallback. Returns 'played' | 'error' so the pump
// stays a thin loop (keeps its cognitive complexity down).
async function playChunkAudio(blob, chunk, refs) {
  if (blob) {
    await playAudioBlob(blob, refs);
    return 'played';
  }
  if (hasSpeechSynthesis()) {
    await speakNative(chunk);
    return 'played';
  }
  return 'error';
}

// Free streaming STT via Web Speech. Recognizes continuously until silence,
// then resolves with the final transcript (or '' on failure/timeout).
function transcribeNative({
  lang = 'pt-BR',
  silenceMs = SILENCE_HOLD_MS,
  maxMs = MAX_CAPTURE_MS,
  signal = undefined,
} = {}) {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) return Promise.resolve(null);
  return new Promise((resolve) => {
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    let finalText = '';
    let lastActivity = Date.now();
    let done = false;
    const finish = (val) => {
      if (!done) {
        done = true;
        cleanup();
        signal?.removeEventListener('abort', abort);
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
        resolve(val);
      }
    };
    const silenceTimer = setInterval(() => {
      if (Date.now() - lastActivity > silenceMs && finalText.trim()) finish(finalText.trim());
      if (Date.now() - lastActivity > maxMs) finish(finalText.trim());
    }, 150);
    const cleanup = () => clearInterval(silenceTimer);
    const abort = () => finish('');
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) { abort(); return; }
    rec.onresult = (e) => {
      lastActivity = Date.now();
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript + ' ';
      }
    };
    rec.onerror = () => {
      cleanup();
      finish(finalText.trim() || null);
    };
    rec.onend = () => {
      cleanup();
      finish(finalText.trim() || '');
    };
    try {
      rec.start();
    } catch {
      cleanup();
      finish(null);
    }
  });
}

// Both helpers below used to let a network-level fetch() rejection (offline,
// DNS, CORS, worker cold-start…) propagate as an unhandled promise rejection
// out of handleStop()/speak() — the state machine froze in 'transcribing' or
// 'speaking' forever with zero UI feedback, which reads exactly like "the
// voice isn't responding". They now catch everything and return a
// {value, error} pair so the caller can always recover.

async function postTranscribe(blob) {
  try {
    const token = getStoredToken();
    const form = new FormData();
    form.append('audio', blob, 'capture.webm');
    form.append('language', 'pt');
    const res = await safeFetch(apiUrl('/api/voice/transcribe'), {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      const errJson = await res.json().catch(() => null);
      return {
        text: '',
        error: errJson?.error || errJson?.message || `Transcrição falhou (${res.status})`,
      };
    }
    const json = await res.json();
    return { text: json?.text || '', error: null };
  } catch (e) {
    return { text: '', error: e?.message || 'Falha de rede na transcrição' };
  }
}

async function postTts(text, voice) {
  try {
    const token = getStoredToken();
    const res = await safeFetch(apiUrl('/api/voice/tts'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      // opus = menor + começa a tocar mais cedo que mp3 no pipeline de voz.
      body: JSON.stringify({ text, voice, format: 'opus' }),
    });
    if (!res.ok) {
      const errJson = await res.json().catch(() => null);
      return {
        blob: null,
        error: errJson?.error || errJson?.message || `TTS falhou (${res.status})`,
      };
    }
    const blob = await res.blob();
    return { blob, error: null };
  } catch (e) {
    return { blob: null, error: e?.message || 'Falha de rede no TTS' };
  }
}

// MediaRecorder + Whisper path — used when Web Speech / Realtime are
// unavailable or fail to start (e.g. unbound Window.fetch Illegal invocation).
async function startMediaRecorderCapture({
  streamRef,
  recorderRef,
  chunksRef,
  audioCtxRef,
  maxTimerRef,
  onStop,
  setState,
  setError,
  enabledRef,
}) {
  try {
    // getUserMedia can block on the mic permission prompt / device acquisition —
    // show 'connecting' until the recorder is actually rolling.
    setState('connecting');
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (!enabledRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    streamRef.current = stream;
    const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
    recorderRef.current = recorder;
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (enabledRef.current && e.data?.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => { if (enabledRef.current) onStop(); };
    recorder.start(200);
    setState('listening');

    const w = /** @type {*} */ (window);
    const ctx = new (w.AudioContext || w.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    let lastVoiceAt = Date.now();
    let heardVoice = false;
    let voicedMs = 0;
    let lastTickAt = Date.now();
    function tick() {
      if (recorder.state !== 'recording') return;
      const now = Date.now();
      const dt = Math.min(100, now - lastTickAt);
      lastTickAt = now;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      if (rms > SILENCE_RMS) {
        heardVoice = true;
        voicedMs += dt;
        lastVoiceAt = now;
      } else if (
        heardVoice &&
        voicedMs >= MIN_VOICED_MS &&
        now - lastVoiceAt > SILENCE_HOLD_MS
      ) {
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
        return;
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    maxTimerRef.current = setTimeout(() => {
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
      }
    }, MAX_CAPTURE_MS);
  } catch (e) {
    if (!enabledRef.current) return;
    setError(e?.message || 'Falha ao iniciar microfone');
    setState('off');
    enabledRef.current = false;
  }
}

/** @returns {Promise<'done'|'fallback'|'skip'>} */
async function tryNativeBrowserStt({ enabledRef, setState, setError, submitTranscript, restartCapture, signal }) {
  if (isElectron() || !getSpeechRecognitionCtor()) return 'skip';
  setState('listening');
  const text = await transcribeNative({ signal });
  if (!enabledRef.current) return 'done';
  if (text === null) {
    // Silent fallback to Whisper — don't banner "usando Whisper".
    return 'fallback';
  }
  if (!text) {
    if (enabledRef.current) restartCapture?.();
    return 'done';
  }
  await submitTranscript(text);
  return 'done';
}

/** @returns {Promise<'done'|'fallback'>} */
async function tryRealtimeConversation(startConversation, enabledRef) {
  if (typeof globalThis.RTCPeerConnection !== 'function') return 'fallback';
  try {
    await startConversation();
    return 'done';
  } catch (err) {
    if (!enabledRef.current) return 'done';
    console.warn(
      '[handsFree] speech-to-speech unavailable — chained STT/TTS fallback:',
      err?.message || err,
    );
    return 'fallback';
  }
}

/** @returns {Promise<'done'|'fallback'>} */
async function tryRealtimeCapture(startRealtimeCapture, enabledRef, _setError) {
  // Prefer OpenAI Realtime over WebRTC everywhere — including Electron.
  // safeFetch fixes Illegal invocation on SDP negotiate. MediaRecorder +
  // whisper-1 is the silent fallback (never surface "Internal Server Error —
  // usando Whisper" as a banner when recovery works).
  if (typeof globalThis.RTCPeerConnection !== 'function') return 'fallback';
  try {
    await startRealtimeCapture();
    return 'done';
  } catch (realtimeErr) {
    if (!enabledRef.current) return 'done';
    console.warn(
      '[handsFree] Realtime unavailable — silent Whisper fallback:',
      realtimeErr?.message || realtimeErr,
    );
    return 'fallback';
  }
}

/**
 * @param {{
 *   wakeWord?: string,
 *   preferConversation?: boolean,
 *   voice?: string,
 *   onSubmitText: (text: string) => Promise<void> | void,
 *   toolContext?: { root?: string, sessionId?: string },
 * }} cfg
 */
export function useHandsFreeMode({ wakeWord, voice = 'cedar', onSubmitText, toolContext, preferConversation = true }) {
  const [state, setState] = useState('off'); // off|connecting|listening|transcribing|thinking|speaking
  const [error, setError] = useState(null);
  const wakeArmedRef = useRef(!!wakeWord);
  const realtimeRef = useRef(null);
  const conversationRef = useRef(null);
  const realtimeMonitorRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const maxTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioRef = useRef(null);
  const audioResolveRef = useRef(null); // resolves the in-flight playback promise on interrupt()
  const interruptedRef = useRef(false); // barge-in flag, distinct from enabledRef (session stays on)
  const replyTimeoutRef = useRef(null); // recovery net if apolo:handsFreeReply never arrives
  const recoveryTimerRef = useRef(null); // delayed restart so the error banner has time to render
  const enabledRef = useRef(false);
  const sessionAbortRef = useRef(null);
  // After Realtime connects but returns empty text (common Electron ICE / gated
  // model failure mode), force MediaRecorder+whisper-1 for the rest of the session
  // instead of looping "Transcrição vazia".
  const forceMediaRecorderRef = useRef(false);
  // Streaming speech pump — the low-latency core. The reply arrives incrementally
  // (apolo:handsFreeReplyDelta as sentences complete, apolo:handsFreeReply as the
  // final): we enqueue each complete sentence and a single pump speaks them in
  // order, prefetching the next sentence's audio while the current one plays. So
  // Apolo starts talking on sentence #1 instead of waiting for the whole reply.
  const ttsQueueRef = useRef([]); // sentences waiting to be spoken (in order)
  const enqueuedCountRef = useRef(0); // # of chunks from the current reply already queued (monotonic dedupe)
  const replyCompleteRef = useRef(false); // the final apolo:handsFreeReply arrived → drain then listen
  const pumpRunningRef = useRef(false); // a pump loop is active (prevents concurrent pumps)
  const playedAnyRef = useRef(false); // did we manage to speak anything this reply
  const speakErrorRef = useRef(null); // last TTS error, surfaced only if nothing played at all
  // Forward ref so callbacks can call startCapture without TDZ ordering issues.
  const startCaptureRef = useRef(null);
  // Session the voice tools read from. A ref so a re-render mid-call does not
  // rebuild the WebRTC session just because the object identity changed.
  const toolContextRef = useRef(toolContext);
  toolContextRef.current = toolContext;

  const cleanupCapture = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    realtimeMonitorRef.current?.stop();
    realtimeMonitorRef.current = null;
    conversationRef.current?.close();
    conversationRef.current = null;
    realtimeRef.current?.close();
    realtimeRef.current = null;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => null);
    }
    audioCtxRef.current = null;
  }, []);

  // Sets error, then restarts capture after a short delay instead of in the
  // same tick — see ERROR_RECOVER_MS above for why the delay matters.
  const recoverAfterError = useCallback((message) => {
    setError(message);
    if (!enabledRef.current) {
      setState('off');
      return;
    }
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = setTimeout(() => {
      recoveryTimerRef.current = null;
      if (enabledRef.current) startCaptureRef.current?.();
    }, ERROR_RECOVER_MS);
  }, []);

  // Clears the speech queue + per-reply bookkeeping. Called when a NEW turn is
  // submitted so a fresh reply never re-speaks the previous one's tail.
  const resetSpeech = useCallback(() => {
    ttsQueueRef.current = [];
    enqueuedCountRef.current = 0;
    replyCompleteRef.current = false;
    playedAnyRef.current = false;
    speakErrorRef.current = null;
  }, []);

  // Runs after the pump loop exits: barge-in → back to listening; reply complete
  // and nothing played (all TTS failed) → surface the error; otherwise → listen.
  const settleAfterPump = useCallback(() => {
    if (interruptedRef.current) {
      interruptedRef.current = false;
      resetSpeech();
      if (enabledRef.current) startCaptureRef.current?.();
      return;
    }
    // Queue drained mid-stream (reply still coming) → stay 'speaking'; a later
    // ingest restarts the pump. Only wrap up once the reply is complete.
    if (!replyCompleteRef.current || ttsQueueRef.current.length > 0) return;
    const failedEntirely = !playedAnyRef.current && speakErrorRef.current;
    const err = speakErrorRef.current;
    resetSpeech();
    if (failedEntirely) recoverAfterError(err);
    else if (enabledRef.current) startCaptureRef.current?.();
  }, [recoverAfterError, resetSpeech]);

  const runSpeechPump = useCallback(async () => {
    if (pumpRunningRef.current) return;
    pumpRunningRef.current = true;
    setState('speaking');
    const refs = { audioRef, audioResolveRef };
    let prefetch = null;
    while (enabledRef.current && !interruptedRef.current && ttsQueueRef.current.length > 0) {
      const chunk = ttsQueueRef.current.shift();
      const { blob, error: ttsErr } = prefetch ? await prefetch : await postTts(chunk, voice);
      // Prefetch the NEXT sentence's audio while this one plays.
      prefetch = ttsQueueRef.current.length ? postTts(ttsQueueRef.current[0], voice) : null;
      if (!enabledRef.current || interruptedRef.current) break;
      const outcome = await playChunkAudio(blob, chunk, refs);
      if (outcome === 'played') playedAnyRef.current = true;
      else speakErrorRef.current = ttsErr;
    }
    pumpRunningRef.current = false;
    settleAfterPump();
  }, [voice, settleAfterPump]);

  // Ingest (part of) a reply: strip to speakable text, split into sentences, and
  // enqueue every COMPLETE sentence not already queued. The trailing (possibly
  // unfinished) sentence is held back until `done` so we never speak half a
  // clause. enqueuedCountRef makes this monotonic — a later, longer snapshot of
  // the same reply only ever ADDS sentences, never re-speaks earlier ones.
  const ingestReply = useCallback(
    (fullText, { done }) => {
      if (!enabledRef.current) return;
      const spoken = toSpokenText(fullText);
      const sentences = spoken ? splitSentences(spoken) : [];
      // While streaming, hold back the final sentence unless the text already ends
      // on a boundary (then it's complete and safe to speak). On `done`, speak all.
      const allComplete = done || endsOnSentenceBoundary(spoken);
      const upTo = allComplete ? sentences.length : Math.max(0, sentences.length - 1);
      for (let i = enqueuedCountRef.current; i < upTo; i += 1) {
        ttsQueueRef.current.push(sentences[i]);
      }
      if (upTo > enqueuedCountRef.current) enqueuedCountRef.current = upTo;
      if (done) replyCompleteRef.current = true;

      if (ttsQueueRef.current.length > 0) {
        runSpeechPump();
      } else if (done && !pumpRunningRef.current) {
        // Nothing speakable in the whole reply (e.g. pure code block) — don't hang.
        resetSpeech();
        if (enabledRef.current) startCaptureRef.current?.();
      }
    },
    [runSpeechPump, resetSpeech],
  );

  // Barge-in: tap the orb while Apolo is speaking to cut the reply short and
  // go straight back to listening — the ChatGPT voice-mode pattern where you
  // don't have to wait out a reply you already have what you need from.
  const interrupt = useCallback(() => {
    if (!enabledRef.current) return;
    if (conversationRef.current) {
      conversationRef.current.interrupt();
      setState('listening');
      return;
    }
    interruptedRef.current = true;
    ttsQueueRef.current = [];
    if (audioResolveRef.current) {
      audioResolveRef.current();
      audioResolveRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        /* ignore */
      }
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
    // If no pump is running (e.g. interrupted between sentences), recover here.
    if (!pumpRunningRef.current) {
      interruptedRef.current = false;
      resetSpeech();
      if (enabledRef.current) startCaptureRef.current?.();
    }
  }, [resetSpeech]);

  // Arms the "reply never came" recovery net right after onSubmitText
  // resolves — cleared by the apolo:handsFreeReply listener below. Without
  // this, an agent-loop error or a dropped event left the orb stuck on
  // 'thinking' forever with zero feedback (the other "not responding" bug).
  const armReplyTimeout = useCallback(() => {
    if (replyTimeoutRef.current) clearTimeout(replyTimeoutRef.current);
    replyTimeoutRef.current = setTimeout(() => {
      replyTimeoutRef.current = null;
      if (!enabledRef.current) return;
      recoverAfterError('Sem resposta do agente — tente de novo');
    }, REPLY_TIMEOUT_MS);
  }, [recoverAfterError]);

  const submitTranscript = useCallback(
    async (text) => {
      const transcript = String(text || '').trim();
      if (!transcript) {
        // Never banner "Transcrição vazia" as a hard error — go back to listening.
        // Caller that got empty from Realtime should set forceMediaRecorderRef first.
        if (enabledRef.current) startCaptureRef.current?.();
        return;
      }
      if (
        wakeArmedRef.current &&
        wakeWord &&
        !transcript.toLowerCase().includes(wakeWord.toLowerCase())
      ) {
        if (enabledRef.current) startCaptureRef.current?.();
        return;
      }
      wakeArmedRef.current = false;
      setState('thinking');
      resetSpeech(); // fresh reply — never inherit the previous turn's queue
      const session = sessionAbortRef.current;
      try {
        await onSubmitText?.(transcript);
        if (enabledRef.current && session === sessionAbortRef.current) armReplyTimeout();
      } catch (e) {
        if (enabledRef.current && session === sessionAbortRef.current) recoverAfterError(e?.message || 'Falha no submit');
      }
    },
    [wakeWord, onSubmitText, armReplyTimeout, recoverAfterError, resetSpeech],
  );

  const handleStop = useCallback(async () => {
    if (!chunksRef.current.length) {
      if (enabledRef.current) startCaptureRef.current?.();
      return;
    }
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    chunksRef.current = [];
    cleanupCapture();
    if (blob.size < 2048) {
      // No real speech captured — go back to listening (no error banner).
      if (enabledRef.current) startCaptureRef.current?.();
      return;
    }
    setState('transcribing');
    const session = sessionAbortRef.current;
    const { text, error: transcribeErr } = await postTranscribe(blob);
    if (!enabledRef.current || session !== sessionAbortRef.current) return;
    const transcript = String(text || '').trim();
    if (!transcript) {
      // Whisper returned empty — retry listen, surface only hard API errors.
      if (transcribeErr) recoverAfterError(transcribeErr);
      else if (enabledRef.current) startCaptureRef.current?.();
      return;
    }
    await submitTranscript(transcript);
  }, [cleanupCapture, submitTranscript, recoverAfterError]);

  const startConversation = useCallback(async () => {
    let conv;
    try {
      conv = new RealtimeConversation({
        sessionUrl: apiUrl('/api/voice/realtime/session'),
        token: getStoredToken(),
        language: 'pt',
        voice,
        onState: (next) => {
          if (!enabledRef.current || conversationRef.current !== conv) return;
          if (['speaking', 'listening', 'thinking'].includes(next)) setState(next);
        },
        onError: (err) => {
          if (conversationRef.current !== conv || !enabledRef.current) return;
          conv.close();
          conversationRef.current = null;
          enabledRef.current = false;
          if (typeof window !== 'undefined') /** @type {*} */ (window).__apoloHandsFree = false;
          setError(err?.message || 'Conexão de voz perdida');
          setState('off');
        },
        onFallback: () => {
          // Worker still on transcription-only (old deploy) — drop S2S and
          // use the chained STT→agent→TTS path for the rest of the session.
          if (conversationRef.current !== conv) return;
          conv.close();
          conversationRef.current = null;
          if (enabledRef.current) startCaptureRef.current?.();
        },
        onToolCall: (toolName, args, { signal }) =>
          runVoiceTool(toolName, args, {
            signal,
            root: toolContextRef.current?.root,
            sessionId: toolContextRef.current?.sessionId,
          }),
      });
      conversationRef.current = conv;
      // Stay 'connecting' through the SDP/ICE negotiation — conv.start() only
      // resolves once the data channel is open. onState('listening') (fired the
      // moment the channel opens) and this post-await set both mark it truly live.
      setState('connecting');
      await conv.start();
      if (!enabledRef.current || conversationRef.current !== conv) {
        conv.close();
        if (conversationRef.current === conv) conversationRef.current = null;
        return;
      }
      setState('listening');
    } catch (startError) {
      conv?.close();
      if (conversationRef.current !== conv) return;
      conversationRef.current = null;
      throw startError;
    }
  }, [voice]);

  const startRealtimeCapture = useCallback(async () => {
    let realtime;
    let liveText = '';
    let finishing = false;
    try {
      const finishTurn = async () => {
        if (finishing || realtimeRef.current !== realtime) return;
        finishing = true;
        realtimeMonitorRef.current?.stop();
        realtimeMonitorRef.current = null;
        setState('transcribing');
        try {
          const finalText = await realtime.stop();
          realtimeRef.current = null;
          const transcript = String(finalText || liveText || '').trim();
          if (!transcript) {
            // Realtime negotiated but produced nothing (ICE / gated model).
            // Switch to MediaRecorder+whisper-1 and listen again — no banner.
            forceMediaRecorderRef.current = true;
            console.warn(
              '[handsFree] Realtime empty transcript — switching to Whisper batch',
            );
            if (enabledRef.current) startCaptureRef.current?.();
            return;
          }
          await submitTranscript(transcript);
        } catch (finishError) {
          realtimeRef.current = null;
          forceMediaRecorderRef.current = true;
          console.warn(
            '[handsFree] Realtime finish failed — switching to Whisper batch:',
            finishError?.message || finishError,
          );
          if (enabledRef.current) startCaptureRef.current?.();
        }
      };

      realtime = new RealtimeTranscriber({
        sessionUrl: apiUrl('/api/voice/realtime/session'),
        token: getStoredToken(),
        language: 'pt',
        onDelta: (delta) => {
          liveText += delta;
        },
        onCompleted: (transcript) => {
          if (transcript) liveText = transcript;
        },
        onError: (realtimeError) => {
          console.warn('[handsFree] Realtime error:', realtimeError?.message);
        },
      });
      realtimeRef.current = realtime;
      // Realtime STT negotiates before it can hear anything — hold 'connecting'
      // until start() resolves, then flip to 'listening'.
      setState('connecting');
      await realtime.start();
      if (!enabledRef.current || realtimeRef.current !== realtime) {
        realtime.close();
        if (realtimeRef.current === realtime) realtimeRef.current = null;
        return;
      }
      setState('listening');
      realtimeMonitorRef.current = startRmsSilenceMonitor(realtime.stream, () => void finishTurn());
    } catch (startError) {
      realtime?.close();
      if (realtimeRef.current !== realtime) return;
      realtimeRef.current = null;
      // Re-throw so startCapture can fall back to MediaRecorder+Whisper
      // instead of killing the whole hands-free loop (Illegal invocation on
      // unbound Window.fetch used to leave the orb stuck / mode off).
      throw startError;
    }
  }, [submitTranscript]);

  const startCapture = useCallback(async () => {
    if (!enabledRef.current) return;
    setError(null);
    const session = sessionAbortRef.current;
    const captureEnabled = {
      get current() { return enabledRef.current && session === sessionAbortRef.current; },
      set current(value) { if (session === sessionAbortRef.current) enabledRef.current = value; },
    };

    const native = await tryNativeBrowserStt({
      enabledRef: captureEnabled,
      signal: session?.signal,
      setState,
      setError,
      submitTranscript,
      restartCapture: () => startCaptureRef.current?.(),
    });
    if (native === 'done' || !captureEnabled.current) return;

    if (!forceMediaRecorderRef.current) {
      const realtime = await tryRealtimeCapture(startRealtimeCapture, captureEnabled, setError);
      if (realtime === 'done' || !captureEnabled.current) return;
      // Realtime failed to start — stay on MediaRecorder for this session.
      forceMediaRecorderRef.current = true;
    }

    await startMediaRecorderCapture({
      streamRef,
      recorderRef,
      chunksRef,
      audioCtxRef,
      maxTimerRef,
      onStop: handleStop,
      setState,
      setError,
      enabledRef: captureEnabled,
    });
  }, [handleStop, startRealtimeCapture, submitTranscript]);

  // Keep ref in sync so callbacks scheduled earlier always reach the latest fn.
  useEffect(() => {
    startCaptureRef.current = startCapture;
  }, [startCapture]);

  const start = useCallback(() => {
    if (enabledRef.current) return;
    enabledRef.current = true;
    sessionAbortRef.current = new AbortController();
    setError(null);
    forceMediaRecorderRef.current = false;
    wakeArmedRef.current = !!wakeWord;
    resetSpeech();
    // Instant feedback on the tap: the session is negotiating (SDP/ICE, mic) and is
    // NOT ready for speech yet. 'connecting' shows a loading orb so the user waits
    // instead of talking into a socket that hasn't opened — flips to 'listening'
    // only once the capture/conversation path is actually live.
    setState('connecting');
    // Global flag so the agent loop only does the (cheap) sentence-boundary work
    // for streaming voice deltas while a voice session is actually open.
    if (typeof window !== 'undefined') /** @type {*} */ (window).__apoloHandsFree = true;
    const begin = async () => {
      const s2s = preferConversation ? await tryRealtimeConversation(startConversation, enabledRef) : 'fallback';
      if (s2s === 'done') return;
      await startCapture();
    };
    begin();
  }, [wakeWord, startCapture, startConversation, resetSpeech, preferConversation]);

  const stop = useCallback(() => {
    enabledRef.current = false;
    sessionAbortRef.current?.abort();
    interruptedRef.current = false;
    if (typeof window !== 'undefined') /** @type {*} */ (window).__apoloHandsFree = false;
    if (replyTimeoutRef.current) {
      clearTimeout(replyTimeoutRef.current);
      replyTimeoutRef.current = null;
    }
    if (audioResolveRef.current) {
      audioResolveRef.current();
      audioResolveRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        /* ignore */
      }
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
    resetSpeech();
    cleanupCapture();
    setState('off');
  }, [cleanupCapture, resetSpeech]);

  // Replies arrive incrementally: apolo:handsFreeReplyDelta as each sentence
  // completes (low latency — speak sentence #1 while #2 is still generating) and
  // apolo:handsFreeReply as the final full text (marks the reply complete). Both
  // carry the FULL accumulated text so far; ingestReply dedupes by sentence.
  useEffect(() => {
    function onDelta(e) {
      const text = e?.detail?.text;
      if (replyTimeoutRef.current) {
        clearTimeout(replyTimeoutRef.current);
        replyTimeoutRef.current = null;
      }
      if (typeof text === 'string' && enabledRef.current) ingestReply(text, { done: false });
    }
    function onReply(e) {
      const text = e?.detail?.text;
      if (replyTimeoutRef.current) {
        clearTimeout(replyTimeoutRef.current);
        replyTimeoutRef.current = null;
      }
      if (typeof text === 'string' && enabledRef.current) ingestReply(text, { done: true });
    }
    window.addEventListener('apolo:handsFreeReplyDelta', onDelta);
    window.addEventListener('apolo:handsFreeReply', onReply);
    return () => {
      window.removeEventListener('apolo:handsFreeReplyDelta', onDelta);
      window.removeEventListener('apolo:handsFreeReply', onReply);
    };
  }, [ingestReply]);

  useEffect(
    () => () => stop(),
    [stop],
  );

  return { state, error, start, stop, interrupt, enabled: enabledRef.current };
}
